import { memo } from 'react';

import { Avatar, type AvatarSizes } from '@air/primitive-avatar';
import { DropdownMenu } from '@air/primitive-dropdown-menu';
import { formatFullName, formatInitials } from '@air/utilities';

import { MainSideNavItemButton } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemButton';
import { MainSideNavItemIcon } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemIcon';
import { MainSideNavItemLabel } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemLabel';
import { MainSideNavItemAccountMenu } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemAccountMenu';
import { useMainSideNavOverlay } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlay';
import { useAccountContext } from '~/providers/AccountProvider';
import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';

const AVATAR_SIZE: AvatarSizes = 24;

export const MainSideNavItemAccount = memo(() => {
  const { data: account } = useAccountContext();
  const { isOpen, onOpenChange } = useMainSideNavOverlay({ id: 'account' });

  if (!account) {
    return (
      <div className="min-w-0 overflow-hidden">
        <MainSideNavItemButton
          aria-hidden
          className="group/account cursor-default hover:text-gray-10 hover:before:opacity-0"
          tabIndex={-1}
          type="button"
        >
          <MainSideNavItemIcon>
            <span
              className="size-6 rounded-full ads:bg-ads-gray-4 bg-gray-4"
              data-testid="MAIN_SIDE_NAV_ACCOUNT_AVATAR"
            />
          </MainSideNavItemIcon>
        </MainSideNavItemButton>
      </div>
    );
  }

  const label = formatFullName(account.firstName, account.lastName) || account.email;

  return (
    <div className="min-w-0 overflow-hidden">
      <DropdownMenu
        align="start"
        avoidCollisions={false}
        className="w-54 min-w-54"
        onOpenChange={onOpenChange}
        open={isOpen}
        side="top"
        trigger={
          <MainSideNavItemButton
            className="group/account"
            data-item-id="account"
            data-main-side-nav-overlay
            type="button"
          >
            <MainSideNavItemIcon>
              <span
                className="rounded-full ads:group-data-[state=open]/account:ring-primary/30 group-data-[state=open]/account:ring-2 group-data-[state=open]/account:ring-altitude-9/30"
                data-testid="MAIN_SIDE_NAV_ACCOUNT_AVATAR"
              >
                <Avatar
                  alt={`${label} avatar`}
                  className="cursor-pointer"
                  colorSeed={account.id}
                  size={AVATAR_SIZE}
                  src={getAvatarImageSrc(account.avatar, AVATAR_SIZE)}
                  text={formatInitials(account.firstName, account.lastName)}
                />
              </span>
            </MainSideNavItemIcon>
            <MainSideNavItemLabel>{label}</MainSideNavItemLabel>
          </MainSideNavItemButton>
        }
      >
        <MainSideNavItemAccountMenu />
      </DropdownMenu>
    </div>
  );
});

MainSideNavItemAccount.displayName = 'MainSideNavItemAccount';
